import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

interface LayoutProps {
  isMobile: boolean;
}

interface ContentProps {
  isMobile: boolean;
}

interface SidebarProps extends LayoutProps {
  open: boolean;
}

export const Layout = styled.div<LayoutProps>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  min-height: 100vh;
  background: transparent;
`;

export const MobileTopbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(14px);
  box-shadow: 0 8px 22px rgba(15, 42, 66, 0.12);
  border-bottom: 1px solid rgba(30, 79, 121, 0.16);
  position: sticky;
  top: 0;
  z-index: 12;
`;

export const DrawerToggle = styled.button`
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  height: 22px;
  width: 28px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;

  span {
    display: block;
    height: 3px;
    width: 100%;
    border-radius: 3px;
    background: var(--teal-700);
  }
`;

export const SidebarContainer = styled.aside<SidebarProps>`
  width: ${({ isMobile }) => (isMobile ? '288px' : '264px')};
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.88) 0%, rgba(236, 251, 255, 0.86) 65%, rgba(227, 255, 245, 0.86) 100%);
  backdrop-filter: blur(16px);
  box-shadow: ${({ isMobile }) =>
    isMobile ? '0 28px 48px rgba(15, 32, 52, 0.24)' : '8px 0 28px rgba(15, 42, 66, 0.12)'};
  border-right: 1px solid rgba(30, 79, 121, 0.18);
  border-radius: ${({ isMobile }) => (isMobile ? '0 18px 18px 0' : '0')};
  padding: ${({ isMobile }) => (isMobile ? '32px 24px 36px' : '48px 28px 40px')};
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: ${({ isMobile }) => (isMobile ? 'fixed' : 'sticky')};
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 30;
  transform: ${({ isMobile, open }) => (isMobile ? (open ? 'translateX(0)' : 'translateX(-110%)') : 'none')};
  transition: transform 0.25s ease;
  max-height: 100vh;
  overflow: hidden;
  pointer-events: ${({ isMobile, open }) => (isMobile ? (open ? 'auto' : 'none') : 'auto')};
`;

export const MobileOverlay = styled.button`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  border: none;
  padding: 0;
  margin: 0;
  z-index: 20;
  cursor: pointer;
`;

export const SidebarContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  padding-right: 8px;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(30, 79, 121, 0.35);
    border-radius: 999px;
  }
`;

export const Topbar = styled.header<LayoutProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ isMobile }) => (isMobile ? 'space-between' : 'center')};
  gap: 16px;
`;

export const CloseButton = styled.button`
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid rgba(30, 79, 121, 0.24);
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  span {
    position: absolute;
    width: 16px;
    height: 2px;
    background: var(--teal-700);
    border-radius: 2px;
  }

  span:first-child {
    transform: rotate(45deg);
  }

  span:last-child {
    transform: rotate(-45deg);
  }
`;

export const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const NavItem = styled(NavLink)`
  color: var(--gray-700);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 11px 16px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &.active {
    background: linear-gradient(120deg, rgba(29, 180, 161, 0.2), rgba(63, 156, 216, 0.2));
    color: var(--blue-700);
    box-shadow: 0 8px 16px rgba(15, 123, 107, 0.14);
  }

  &:hover {
    background: linear-gradient(120deg, rgba(219, 240, 255, 0.95), rgba(220, 255, 251, 0.95));
    transform: translateX(3px);
  }
`;

export const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: none;
  background: transparent;
  color: var(--gray-700);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }

  &.active,
  &:hover {
    background: linear-gradient(120deg, rgba(219, 240, 255, 0.95), rgba(220, 255, 251, 0.95));
    color: var(--blue-700);
    box-shadow: 0 8px 16px rgba(15, 123, 107, 0.12);
    transform: translateX(3px);
  }

  &.active::after {
    transform: rotate(-135deg);
  }
`;

export const Submenu = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px 0 8px 12px;
  border-left: 2px solid rgba(15, 123, 107, 0.28);
  padding-left: 12px;
  gap: 4px;
`;

export const SubmenuItem = styled(NavLink)`
  color: var(--gray-600);
  font-size: 0.9rem;
  padding: 6px 12px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;

  &.active {
    background: linear-gradient(120deg, rgba(219, 240, 255, 0.95), rgba(220, 255, 251, 0.95));
    color: var(--blue-700);
  }

  &:hover {
    background: var(--blue-100);
    color: var(--blue-700);
  }
`;

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

export const Content = styled.main<ContentProps>`
  flex: 1;
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '96px 20px 32px' : '48px 56px 32px')};
  position: relative;
  z-index: 2;
`;

export const FooterBar = styled.footer<LayoutProps>`
  display: flex;
  justify-content: ${({ isMobile }) => (isMobile ? 'center' : 'flex-start')};
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '18px 20px' : '24px 56px')};
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(30, 79, 121, 0.14);
  box-shadow: ${({ isMobile }) =>
    isMobile ? '0px -6px 14px rgba(0, 0, 0, 0.08)' : '0px -6px 14px rgba(15, 42, 66, 0.06)'};

  p {
    font-style: normal;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.375rem;
    color: var(--gray-400);
  }
`;
